import { useEffect, useRef } from 'react';
import { useAppDispatch } from '@/app/store/hooks';
import { store } from '@/app/store';
import { startRefreshing, refreshFailed, refreshSuccess, updateTokens, logout } from '@/app/store/slices/authSlice';
import { authApi } from '@/services/endpoints/auth';
import { rawAxios } from '@/services/api/axios';
import { getTokenExpiration, isTokenExpired } from '@/utils/token';
import { JMS_KEYS } from '@/config/jmsStorage';

const REFRESH_CHECK_INTERVAL = 30000;
const REFRESH_BEFORE_EXPIRY = 120000;

export function useTokenRefresh() {
  const dispatch = useAppDispatch();
  const isRefreshingRef = useRef(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const checkAndRefresh = async () => {
      if (isRefreshingRef.current) {
        return;
      }

      const state = store.getState();
      const accessToken = state.auth.tokens.accessToken;
      const refreshToken = state.auth.tokens.refreshToken;

      if (!accessToken || !refreshToken) {
        return;
      }

      if (isTokenExpired(accessToken)) {
        const isOnline = navigator.onLine;
        if (isOnline) {
          dispatch(logout());
          localStorage.removeItem(JMS_KEYS.user);
          localStorage.removeItem(JMS_KEYS.accessToken);
          localStorage.removeItem(JMS_KEYS.refreshToken);
        }
        return;
      }

      const expiration = getTokenExpiration(accessToken);
      if (!expiration) {
        return;
      }

      const timeUntilExpiry = expiration - Date.now();
      if (timeUntilExpiry > REFRESH_BEFORE_EXPIRY) {
        return;
      }

      isRefreshingRef.current = true;
      dispatch(startRefreshing());

      try {
        const currentState = store.getState();
        const currentRefreshToken = currentState.auth.tokens.refreshToken;

        if (!currentRefreshToken) {
          throw new Error('No refresh token available');
        }

        const response = await authApi.refresh({ refreshToken: currentRefreshToken }, rawAxios);
        // Refresh returns the tokens FLAT on data (not nested under `tokens`).
        // Keep the current refresh token if the server did not rotate it in the body.
        const { accessToken: newAccessToken, refreshToken: rotatedRefreshToken } = response.data;
        const newRefreshToken = rotatedRefreshToken ?? currentRefreshToken;

        dispatch(updateTokens({ accessToken: newAccessToken, refreshToken: newRefreshToken }));
        dispatch(refreshSuccess());

        localStorage.setItem(JMS_KEYS.accessToken, newAccessToken);
        localStorage.setItem(JMS_KEYS.refreshToken, newRefreshToken);
      } catch (error: any) {
        const isNetworkError =
          !error.response ||
          error.code === 'ERR_NETWORK' ||
          error.message?.includes('network');

        if (isNetworkError) {
          dispatch(refreshFailed('Network error during token refresh'));
        } else {
          const isOnline = navigator.onLine;
          if (isOnline) {
            dispatch(refreshFailed('Token refresh failed'));
            dispatch(logout());
            localStorage.removeItem(JMS_KEYS.user);
            localStorage.removeItem(JMS_KEYS.accessToken);
            localStorage.removeItem(JMS_KEYS.refreshToken);
          }
        }
      } finally {
        isRefreshingRef.current = false;
      }
    };

    intervalRef.current = setInterval(checkAndRefresh, REFRESH_CHECK_INTERVAL);
    checkAndRefresh();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [dispatch]);
}
